import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    Box,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';

import { SCUIButton, SCUILoadingLinear, SCUIPagination } from '../../../atomic/atoms';
import { SCUIBreadCrumb, SCUISelectBox } from '../../../atomic/molecules';
import { SCUIContent } from '../../../atomic/organisms';
import {
    RawImageReviewQuery,
    type RawImageReviewItem,
    type WorkAssignmentAssigneeOption,
    type WorkAssignmentCapabilities,
} from '../../../services/studio/StudioQuery/RawImageReview/RawImageReviewQuery.services';
import { WorkAssignmentQuery } from '../../../services/studio/StudioQuery/WorkAssignment/WorkAssignmentQuery.services';
import { RawImageUploadCommand } from '../../../services/studio/StudioCommand/RawImageUpload/RawImageUploadCommand.services';
import { WorkAssignmentCommand } from '../../../services/studio/StudioCommand/WorkAssignment/WorkAssignmentCommand.services';
import { AiIntegrationCommand } from '../../../services/studio/StudioCommand/AiIntegration/AiIntegrationCommand.services';
import { GetS3PresignedDownloadUrls } from '../../../services/query/aiPrompt/S3BrowserQuery.services';
import { toThumbnailS3Key } from '../../utils/studioShotS3Path';
import { RetryableZoomableImage, type PreviewUrlPair } from './RetryableZoomableImage';

const WORK_ASSIGNMENT_FEATURE_KEY = 'rawImageReview';
const PAGE_SIZE = 20;
const THUMBNAIL_HEIGHT = 150;
const DIALOG_IMAGE_HEIGHT = 280;
const SIBLING_PAGE_SIZE = 50;

const PHOTO_TYPE_TABS = ['FrontShot', 'BackShot', 'DetailShot'] as const;

type PhotoTypeCode = (typeof PHOTO_TYPE_TABS)[number];

// Same key rule as StudioAuditUserName.NormalizeAssignee on the backend (case + Turkish I/ı/İ insensitive).
const normalizeAssignee = (value?: string) =>
    (value ?? '')
        .trim()
        .replace(/[İIı]/g, 'i')
        .toLowerCase();

type BarcodeGroup = {
    barcode: string;
    primary: RawImageReviewItem;
    items: RawImageReviewItem[];
};

export const RawImageFrameControl = () => {
    const { t } = useTranslation();

    const [activePhotoType, setActivePhotoType] = useState<PhotoTypeCode>('FrontShot');
    const [items, setItems] = useState<RawImageReviewItem[]>([]);
    const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
    const [totalCount, setTotalCount] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [loading, setLoading] = useState(false);
    const [capabilities, setCapabilities] = useState<WorkAssignmentCapabilities | null>(null);
    const [assigneeOptions, setAssigneeOptions] = useState<WorkAssignmentAssigneeOption[]>([]);
    const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});
    const thumbnailUrlsRef = useRef<Record<string, string>>({});
    const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
    const imageUrlsRef = useRef<Record<string, string>>({});
    const siblingCacheRef = useRef<Record<string, RawImageReviewItem[]>>({});
    const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; item: RawImageReviewItem } | null>(null);
    const [activeProductBarcode, setActiveProductBarcode] = useState<string | null>(null);
    const [productFrames, setProductFrames] = useState<RawImageReviewItem[]>([]);
    const [productDialogLoading, setProductDialogLoading] = useState(false);
    const [deletingRawImageId, setDeletingRawImageId] = useState<number | null>(null);
    const [deletingBarcode, setDeletingBarcode] = useState<string | null>(null);
    const [assigningBarcode, setAssigningBarcode] = useState<string | null>(null);
    const [startingBatch, setStartingBatch] = useState(false);

    const assigneeSelectList = useMemo(
        () =>
            assigneeOptions.map((option) => ({
                id: option.username,
                value: option.displayName || option.username,
            })),
        [assigneeOptions],
    );

    const assigneeOptionByKey = useMemo(
        () =>
            new Map(
                assigneeOptions.map((option) => [
                    normalizeAssignee(option.username),
                    {
                        username: option.username,
                        displayName: option.displayName || option.username,
                    },
                ]),
            ),
        [assigneeOptions],
    );

    const barcodeGroups = useMemo((): BarcodeGroup[] => {
        const map = new Map<string, RawImageReviewItem[]>();
        items.forEach((item) => {
            const key = item.barcode || String(item.rawImageId);
            const existing = map.get(key);
            if (existing) {
                existing.push(item);
            } else {
                map.set(key, [item]);
            }
        });

        return Array.from(map.entries()).map(([barcode, groupItems]) => ({
            barcode,
            primary: groupItems[0],
            items: groupItems,
        }));
    }, [items]);

    const getPhotoTypeLabel = useCallback(
        (photoTypeCode: string, displayName?: string) =>
            displayName || t(`studio.bulkImageUpload.tabs.${photoTypeCode}`, photoTypeCode),
        [t],
    );

    const resolveThumbnailKey = useCallback((item: RawImageReviewItem) => {
        if (item.thumbnailPath) {
            return item.thumbnailPath;
        }
        return item.filePath ? toThumbnailS3Key(item.filePath) : '';
    }, []);

    const readPreviewUrlPair = useCallback((item: RawImageReviewItem): PreviewUrlPair => {
        const thumbKey = resolveThumbnailKey(item);
        return {
            thumbnailSrc: (thumbKey && thumbnailUrlsRef.current[thumbKey]) || '',
            originalSrc: (item.filePath && imageUrlsRef.current[item.filePath]) || '',
        };
    }, [resolveThumbnailKey]);

    const loadPreviewPresignedUrls = useCallback(async (
        list: RawImageReviewItem[],
        options?: { force?: boolean },
    ) => {
        const force = Boolean(options?.force);
        const thumbKeys = new Set<string>();
        const originalKeys = new Set<string>();

        list.forEach((item) => {
            const thumbKey = resolveThumbnailKey(item);
            if (thumbKey && (force || !thumbnailUrlsRef.current[thumbKey])) {
                thumbKeys.add(thumbKey);
            }
            if (item.filePath && (force || !imageUrlsRef.current[item.filePath])) {
                originalKeys.add(item.filePath);
            }
        });

        const keys = Array.from(new Set([...thumbKeys, ...originalKeys]));
        if (keys.length === 0) {
            return;
        }

        try {
            const presigned = await GetS3PresignedDownloadUrls(keys);
            const nextThumbs: Record<string, string> = {};
            const nextOriginals: Record<string, string> = {};

            presigned.items.forEach((entry) => {
                if (!entry.key || !entry.url) {
                    return;
                }
                if (thumbKeys.has(entry.key)) {
                    nextThumbs[entry.key] = entry.url;
                }
                if (originalKeys.has(entry.key)) {
                    nextOriginals[entry.key] = entry.url;
                }
            });

            if (Object.keys(nextThumbs).length > 0) {
                thumbnailUrlsRef.current = { ...thumbnailUrlsRef.current, ...nextThumbs };
                setThumbnailUrls((prev) => ({ ...prev, ...nextThumbs }));
            }

            if (Object.keys(nextOriginals).length > 0) {
                imageUrlsRef.current = { ...imageUrlsRef.current, ...nextOriginals };
                setImageUrls((prev) => ({ ...prev, ...nextOriginals }));
            }
        } catch {
            // Preview failure should not block the page.
        }
    }, [resolveThumbnailKey]);

    const refreshPreviewUrlPair = useCallback(async (item: RawImageReviewItem): Promise<PreviewUrlPair> => {
        await loadPreviewPresignedUrls([item], { force: true });
        return readPreviewUrlPair(item);
    }, [loadPreviewPresignedUrls, readPreviewUrlPair]);

    const loadOriginalPresignedUrls = useCallback(async (list: RawImageReviewItem[]) => {
        await loadPreviewPresignedUrls(list);
    }, [loadPreviewPresignedUrls]);

    const clearSiblingCache = () => {
        siblingCacheRef.current = {};
    };

    const fetchFramesForBarcode = useCallback(
        async (barcode: string): Promise<RawImageReviewItem[]> => {
            if (siblingCacheRef.current[barcode]) {
                const cached = siblingCacheRef.current[barcode];
                await loadOriginalPresignedUrls(cached);
                return cached;
            }

            const response = await RawImageReviewQuery.GetItems({
                pageNumber: 1,
                pageSize: SIBLING_PAGE_SIZE,
                barcode,
            });

            const frames = response.items ?? [];
            siblingCacheRef.current[barcode] = frames;
            await loadOriginalPresignedUrls(frames);
            return frames;
        },
        [loadOriginalPresignedUrls],
    );

    const loadItems = useCallback(async () => {
        setLoading(true);
        try {
            const response = await RawImageReviewQuery.GetItems({
                pageNumber,
                pageSize: PAGE_SIZE,
                photoTypeCode: activePhotoType,
            });

            const nextItems = response.items ?? [];
            setItems(nextItems);
            setTabCounts(response.tabCounts ?? {});
            setTotalCount(response.totalCount ?? 0);
            setCapabilities(response.capabilities ?? null);
            await loadPreviewPresignedUrls(nextItems);
        } catch {
            Swal.fire(t('studio.rawImageFrameControl.loadError'), '', 'error');
        } finally {
            setLoading(false);
        }
    }, [activePhotoType, loadPreviewPresignedUrls, pageNumber, t]);

    useEffect(() => {
        void loadItems();
    }, [loadItems]);

    // Assignee list does not change between page loads, so it is fetched once (and only for supervisors)
    // instead of riding along with every list response.
    const canManageAssignments = capabilities?.canManageAssignments === true;
    useEffect(() => {
        if (!canManageAssignments) {
            return;
        }

        let cancelled = false;
        WorkAssignmentQuery.GetAssignees(WORK_ASSIGNMENT_FEATURE_KEY)
            .then((options) => {
                if (!cancelled) {
                    setAssigneeOptions(options);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    Swal.fire(t('studio.rawImageFrameControl.loadAssigneesError'), '', 'error');
                }
            });

        return () => {
            cancelled = true;
        };
    }, [canManageAssignments, t]);

    const handleDeleteFile = async (item: RawImageReviewItem) => {
        if (deletingRawImageId !== null || deletingBarcode !== null) {
            return;
        }

        const confirmation = await Swal.fire({
            title: t('studio.rawImageFrameControl.deleteConfirmTitle'),
            html: t('studio.rawImageFrameControl.deleteConfirmText', {
                fileName: item.fileName,
                barcode: item.barcode,
            }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('studio.rawImageFrameControl.deleteConfirm'),
            cancelButtonText: t('studio.rawImageFrameControl.deleteCancel'),
            confirmButtonColor: '#d32f2f',
        });

        if (!confirmation.isConfirmed) {
            return;
        }

        setDeletingRawImageId(item.rawImageId);
        try {
            const result = await RawImageUploadCommand.DeleteFile({
                rawImageId: item.rawImageId,
                barcode: item.barcode,
            });

            if (!result.success) {
                Swal.fire(result.message ?? t('studio.rawImageFrameControl.deleteFailed'), '', 'error');
                return;
            }

            clearSiblingCache();

            if (activeProductBarcode === item.barcode) {
                const frames = await fetchFramesForBarcode(item.barcode);
                setProductFrames(frames);
                if (frames.length === 0) {
                    setActiveProductBarcode(null);
                    setProductDialogLoading(false);
                }
            }

            await loadItems();
            Swal.fire(t('studio.rawImageFrameControl.deleteSuccess'), '', 'success');
        } catch {
            Swal.fire(t('studio.rawImageFrameControl.deleteFailed'), '', 'error');
        } finally {
            setDeletingRawImageId(null);
        }
    };

    const handleDeleteProduct = async (barcode: string) => {
        if (deletingRawImageId !== null || deletingBarcode !== null || !barcode) {
            return;
        }

        let frames: RawImageReviewItem[] = [];
        try {
            delete siblingCacheRef.current[barcode];
            frames = await fetchFramesForBarcode(barcode);
        } catch {
            Swal.fire(t('studio.rawImageFrameControl.loadError'), '', 'error');
            return;
        }

        if (frames.length === 0) {
            Swal.fire(t('studio.rawImageFrameControl.noItems'), '', 'info');
            return;
        }

        const confirmation = await Swal.fire({
            title: t('studio.rawImageFrameControl.deleteProductConfirmTitle'),
            html: t('studio.rawImageFrameControl.deleteProductConfirmText', {
                barcode,
                count: frames.length,
            }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('studio.rawImageFrameControl.deleteConfirm'),
            cancelButtonText: t('studio.rawImageFrameControl.deleteCancel'),
            confirmButtonColor: '#d32f2f',
        });

        if (!confirmation.isConfirmed) {
            return;
        }

        setDeletingBarcode(barcode);
        try {
            const failedMessages: string[] = [];

            for (const frame of frames) {
                try {
                    const result = await RawImageUploadCommand.DeleteFile({
                        rawImageId: frame.rawImageId,
                        barcode: frame.barcode,
                    });

                    if (!result.success) {
                        failedMessages.push(
                            result.message
                                ?? `${frame.fileName || frame.rawImageId}`,
                        );
                    }
                } catch {
                    failedMessages.push(frame.fileName || String(frame.rawImageId));
                }
            }

            clearSiblingCache();

            if (activeProductBarcode === barcode) {
                setActiveProductBarcode(null);
                setProductFrames([]);
                setProductDialogLoading(false);
            }

            await loadItems();

            if (failedMessages.length > 0) {
                Swal.fire(
                    t('studio.rawImageFrameControl.deleteProductPartialFailed', {
                        failed: failedMessages.length,
                        total: frames.length,
                    }),
                    failedMessages.slice(0, 5).join('\n'),
                    'warning',
                );
                return;
            }

            Swal.fire(
                t('studio.rawImageFrameControl.deleteProductSuccess', { count: frames.length }),
                '',
                'success',
            );
        } catch {
            Swal.fire(t('studio.rawImageFrameControl.deleteFailed'), '', 'error');
        } finally {
            setDeletingBarcode(null);
        }
    };

    const handleMovePhotoType = async (item: RawImageReviewItem, targetPhotoTypeCode: string) => {
        setMenuAnchor(null);
        try {
            const result = await RawImageUploadCommand.UpdatePhotoType({
                rawImageId: item.rawImageId,
                barcode: item.barcode,
                photoTypeCode: targetPhotoTypeCode,
            });

            if (!result.success) {
                Swal.fire(result.message ?? t('studio.rawImageFrameControl.moveFailed'), '', 'error');
                return;
            }

            Swal.fire(t('studio.rawImageFrameControl.moveSuccess'), '', 'success');
            clearSiblingCache();
            if (activeProductBarcode) {
                const frames = await fetchFramesForBarcode(activeProductBarcode);
                setProductFrames(frames);
            }
            void loadItems();
        } catch {
            Swal.fire(t('studio.rawImageFrameControl.moveFailed'), '', 'error');
        }
    };

    // Assigns every frame of the barcode (front/back/detail), not just the one on the card.
    const handleAssignBarcode = async (barcode: string, targetAssignee: string) => {
        if (!barcode || !targetAssignee || assigningBarcode !== null) {
            return;
        }

        setAssigningBarcode(barcode);
        try {
            delete siblingCacheRef.current[barcode];
            const frames = await fetchFramesForBarcode(barcode);
            const itemIds = frames.map((frame) => frame.rawImageId);

            if (itemIds.length === 0) {
                Swal.fire(t('studio.rawImageFrameControl.noItems'), '', 'info');
                return;
            }

            const result = await WorkAssignmentCommand.Reassign(WORK_ASSIGNMENT_FEATURE_KEY, {
                itemIds,
                targetAssignee,
            });

            if (!result.success) {
                Swal.fire(result.message ?? t('studio.rawImageFrameControl.reassignFailed'), '', 'error');
                return;
            }

            // The supervisor still sees the item after reassigning, so patch local state instead of
            // re-fetching the list and every sibling frame.
            const patchAssignee = (entry: RawImageReviewItem) =>
                entry.barcode === barcode ? { ...entry, assignee: targetAssignee } : entry;
            setItems((prev) => prev.map(patchAssignee));
            siblingCacheRef.current[barcode] = frames.map(patchAssignee);
            setProductFrames((prev) => prev.map(patchAssignee));
            Swal.fire({
                title: t('studio.rawImageFrameControl.reassignSuccess'),
                icon: 'success',
                timer: 1200,
                showConfirmButton: false,
            });
        } catch {
            Swal.fire(t('studio.rawImageFrameControl.reassignFailed'), '', 'error');
        } finally {
            setAssigningBarcode(null);
        }
    };

    const openProductDialog = async (barcode: string) => {
        setActiveProductBarcode(barcode);
        setProductDialogLoading(true);
        try {
            const frames = await fetchFramesForBarcode(barcode);
            setProductFrames(frames);
        } catch {
            setProductFrames([]);
            Swal.fire(t('studio.rawImageFrameControl.loadError'), '', 'error');
        } finally {
            setProductDialogLoading(false);
        }
    };

    const closeProductDialog = () => {
        setActiveProductBarcode(null);
        setProductFrames([]);
        setProductDialogLoading(false);
    };

    const handleStartBatch = async () => {
        const barcodes = barcodeGroups.map((g) => g.barcode).filter(Boolean);
        const confirm = await Swal.fire({
            title: t('studio.rawImageFrameControl.batchConfirmTitle'),
            text: t('studio.rawImageFrameControl.batchConfirmText', { count: barcodes.length || '—' }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('studio.rawImageFrameControl.startBatch'),
            cancelButtonText: t('studio.rawImageFrameControl.deleteCancel'),
        });

        if (!confirm.isConfirmed) {
            return;
        }

        setStartingBatch(true);
        try {
            const result = await AiIntegrationCommand.Produce({
                barcodes: barcodes.length > 0 ? barcodes : undefined,
            });

            if (result.status === 'Failed' || !AiIntegrationCommand.isProduceSuccess(result.status)) {
                if (result.status === 'Skipped') {
                    Swal.fire(
                        result.message ?? t('studio.rawImageFrameControl.batchSkipped'),
                        '',
                        'info',
                    );
                    return;
                }

                Swal.fire(
                    result.message ?? t('studio.rawImageFrameControl.batchFailed'),
                    '',
                    'error',
                );
                return;
            }

            await Swal.fire({
                title: t('studio.rawImageFrameControl.batchSuccess'),
                text: t('studio.rawImageFrameControl.batchSuccessDetail', {
                    produced: result.producedBarcodeCount ?? 0,
                    images: result.processedImageCount ?? 0,
                    skipped: result.skippedBarcodeCount ?? 0,
                }),
                icon: 'success',
            });
            siblingCacheRef.current = {};
            await loadItems();
        } catch {
            Swal.fire(t('studio.rawImageFrameControl.batchFailed'), '', 'error');
        } finally {
            setStartingBatch(false);
        }
    };

    const otherPhotoTypes = (current: string) =>
        PHOTO_TYPE_TABS.filter((code) => code !== current);

    const framesByType = useMemo(() => {
        const map: Record<string, RawImageReviewItem[]> = {
            FrontShot: [],
            BackShot: [],
            DetailShot: [],
        };

        productFrames.forEach((frame) => {
            const key = PHOTO_TYPE_TABS.includes(frame.photoTypeCode as PhotoTypeCode)
                ? frame.photoTypeCode
                : 'DetailShot';
            map[key] = [...(map[key] ?? []), frame];
        });

        return map;
    }, [productFrames]);

    const renderFrameSlot = (photoTypeCode: PhotoTypeCode, frames: RawImageReviewItem[]) => {
        const label = getPhotoTypeLabel(photoTypeCode);

        return (
            <Box
                key={photoTypeCode}
                sx={{
                    minWidth: 0,
                    p: 1.5,
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                }}
            >
                <Typography
                    sx={{
                        mb: 1.5,
                        fontWeight: 900,
                        fontSize: '1.05rem',
                        letterSpacing: 0.4,
                        textTransform: 'uppercase',
                        color: 'text.primary',
                    }}
                >
                    {label}
                </Typography>

                {frames.length === 0 ? (
                    <Box
                        sx={{
                            height: DIALOG_IMAGE_HEIGHT,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 1,
                            border: '1px dashed',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" textAlign="center" px={2}>
                            {t('studio.rawImageFrameControl.missingFrame', { frame: label })}
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={1.5}>
                        {frames.map((item) => (
                            <Box key={item.rawImageId} sx={{ position: 'relative', minWidth: 0 }}>
                                <RetryableZoomableImage
                                    imageKey={`dialog-${item.rawImageId}`}
                                    thumbnailSrc={thumbnailUrls[resolveThumbnailKey(item)] || ''}
                                    originalSrc={imageUrls[item.filePath] || ''}
                                    alt={item.fileName}
                                    onRetryRequest={() => refreshPreviewUrlPair(item)}
                                    containerSx={{
                                        width: '100%',
                                        height: DIALOG_IMAGE_HEIGHT,
                                        borderRadius: 1,
                                        bgcolor: 'background.paper',
                                        border: 1,
                                        borderColor: 'divider',
                                    }}
                                    imageSx={{ objectFit: 'contain', p: 0.5 }}
                                />
                                <IconButton
                                    size="small"
                                    color="error"
                                    disabled={deletingRawImageId !== null || deletingBarcode !== null}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 48,
                                        bgcolor: 'background.paper',
                                        boxShadow: 1,
                                        '&:hover': { bgcolor: 'background.paper' },
                                    }}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        void handleDeleteFile(item);
                                    }}
                                    aria-label={t('studio.rawImageFrameControl.deleteConfirm')}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        bgcolor: 'background.paper',
                                        boxShadow: 1,
                                        '&:hover': { bgcolor: 'background.paper' },
                                    }}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setMenuAnchor({ el: event.currentTarget, item });
                                    }}
                                >
                                    <MoreVertIcon fontSize="small" />
                                </IconButton>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                    title={item.fileName}
                                    sx={{ display: 'block', mt: 0.75 }}
                                >
                                    {item.fileName}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Box>
        );
    };

    return (
        <SCUIContent>
            <SCUIBreadCrumb
                title={t('studio.rawImageFrameControl.title')}
                subTitle={t('studio.rawImageFrameControl.subtitle')}
            />

            {loading && <SCUILoadingLinear />}

            <Paper sx={{ p: { xs: 2, md: 3 } }}>
                <Tabs
                    value={activePhotoType}
                    onChange={(_, value: PhotoTypeCode) => {
                        setActivePhotoType(value);
                        setPageNumber(1);
                        clearSiblingCache();
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        mb: 2,
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiTab-root': {
                            fontWeight: 800,
                            fontSize: '1rem',
                            textTransform: 'none',
                            minHeight: 52,
                            letterSpacing: 0.2,
                        },
                    }}
                >
                    {PHOTO_TYPE_TABS.map((code) => {
                        const tabLabel = getPhotoTypeLabel(code);
                        const count = tabCounts[code] ?? 0;

                        return (
                            <Tab
                                key={code}
                                value={code}
                                label={count > 0 ? `${tabLabel} (${count})` : tabLabel}
                            />
                        );
                    })}
                </Tabs>

                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 900, fontSize: '1.15rem', letterSpacing: 0.2 }}>
                        {getPhotoTypeLabel(activePhotoType)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('studio.rawImageFrameControl.panelHint')}
                    </Typography>
                </Box>

                {barcodeGroups.length === 0 && !loading && (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                        {t('studio.rawImageFrameControl.noItems')}
                    </Typography>
                )}

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: 'repeat(auto-fill, minmax(160px, 1fr))',
                            sm: 'repeat(auto-fill, minmax(180px, 220px))',
                        },
                        gap: 2,
                        justifyContent: 'start',
                    }}
                >
                    {barcodeGroups.map((group) => {
                        const availableTypes = new Set(
                            group.primary.availablePhotoTypeCodes.length > 0
                                ? group.primary.availablePhotoTypeCodes
                                : group.items.map((frame) => frame.photoTypeCode),
                        );
                        const assigneeKey = normalizeAssignee(group.primary.assignee);
                        const assigneeOption = assigneeOptionByKey.get(assigneeKey);
                        const isAssigning = assigningBarcode === group.barcode;
                        const activeTypeLabel = getPhotoTypeLabel(
                            group.primary.photoTypeCode,
                            group.primary.photoTypeDisplayName,
                        );

                        return (
                            <Paper
                                key={group.barcode}
                                variant="outlined"
                                sx={{
                                    p: 1.25,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minWidth: 0,
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper',
                                    transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
                                    '&:hover': {
                                        boxShadow: 3,
                                        borderColor: 'primary.light',
                                    },
                                }}
                            >
                                <Box sx={{ position: 'relative', mb: 1 }}>
                                    <RetryableZoomableImage
                                        imageKey={`grid-${group.primary.rawImageId}`}
                                        thumbnailSrc={
                                            thumbnailUrls[resolveThumbnailKey(group.primary)] || ''
                                        }
                                        originalSrc={imageUrls[group.primary.filePath] || ''}
                                        alt={group.primary.fileName}
                                        onPreview={() => {
                                            void openProductDialog(group.barcode);
                                        }}
                                        onRetryRequest={() => refreshPreviewUrlPair(group.primary)}
                                        containerSx={{
                                            width: '100%',
                                            height: THUMBNAIL_HEIGHT,
                                            borderRadius: 1,
                                            bgcolor: 'grey.100',
                                            border: 1,
                                            borderColor: 'divider',
                                        }}
                                        imageSx={{ objectFit: 'contain', p: 0.5 }}
                                    />
                                    <IconButton
                                        size="small"
                                        color="error"
                                        disabled={deletingRawImageId !== null || deletingBarcode !== null}
                                        sx={{
                                            position: 'absolute',
                                            top: 4,
                                            right: 4,
                                            zIndex: 4,
                                            bgcolor: 'background.paper',
                                            boxShadow: 1,
                                            '&:hover': { bgcolor: 'background.paper' },
                                        }}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            void handleDeleteProduct(group.barcode);
                                        }}
                                        aria-label={t('studio.rawImageFrameControl.deleteConfirm')}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                <Typography
                                    sx={{
                                        fontWeight: 900,
                                        fontSize: '0.95rem',
                                        letterSpacing: 0.3,
                                        textTransform: 'uppercase',
                                        color: 'text.primary',
                                        lineHeight: 1.2,
                                        mb: 0.5,
                                    }}
                                >
                                    {activeTypeLabel}
                                </Typography>

                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
                                    <Typography
                                        variant="body2"
                                        fontWeight={700}
                                        noWrap
                                        title={group.barcode}
                                        sx={{ flex: 1, minWidth: 0 }}
                                    >
                                        {group.barcode}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        color="success"
                                        label={t('studio.rawImageFrameControl.statusReady')}
                                        sx={{ height: 20, fontSize: '0.65rem', flexShrink: 0 }}
                                    />
                                </Stack>

                                <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                                    {PHOTO_TYPE_TABS.map((code) => {
                                        const present = availableTypes.has(code);
                                        return (
                                            <Box
                                                key={code}
                                                title={getPhotoTypeLabel(code)}
                                                sx={{
                                                    px: 0.75,
                                                    py: 0.25,
                                                    borderRadius: 0.75,
                                                    fontSize: '0.65rem',
                                                    fontWeight: 800,
                                                    letterSpacing: 0.2,
                                                    bgcolor: present ? 'grey.900' : 'grey.200',
                                                    color: present ? '#fff' : 'text.disabled',
                                                }}
                                            >
                                                {getPhotoTypeLabel(code)}
                                            </Box>
                                        );
                                    })}
                                </Stack>

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                    title={group.primary.assignee}
                                    sx={{ display: 'block', mt: 1 }}
                                >
                                    {t('studio.rawImageFrameControl.assignedTo', {
                                        assignee: assigneeOption?.displayName || group.primary.assignee || '-',
                                    })}
                                </Typography>

                                {capabilities?.canManageAssignments && (
                                    <Box sx={{ mt: 0.5 }}>
                                        <SCUISelectBox
                                            label={t('studio.rawImageFrameControl.reassignTarget')}
                                            showHeadLabel={false}
                                            size="s"
                                            value={assigneeOption?.username ?? ''}
                                            list={assigneeSelectList}
                                            selectList={
                                                assigneeOption
                                                    ? [{ id: assigneeOption.username, value: assigneeOption.displayName }]
                                                    : []
                                            }
                                            stateName={`assignee-${group.barcode}`}
                                            disabled={isAssigning || assigningBarcode !== null}
                                            isLoading={isAssigning}
                                            onChange={(selected) => {
                                                const next = selected?.[0]?.id;
                                                if (typeof next === 'string' && next !== assigneeOption?.username) {
                                                    void handleAssignBarcode(group.barcode, next);
                                                }
                                            }}
                                        />
                                    </Box>
                                )}
                            </Paper>
                        );
                    })}
                </Box>

                {totalCount > PAGE_SIZE && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <SCUIPagination
                            rowsPerPageOptions={[PAGE_SIZE]}
                            count={totalCount}
                            rowsPerPage={PAGE_SIZE}
                            page={pageNumber - 1}
                            setPage={(nextPage: number) => setPageNumber(nextPage + 1)}
                        />
                    </Box>
                )}

                <Divider sx={{ my: 3 }} />

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={2}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                            {t('studio.rawImageFrameControl.batchReadyTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('studio.rawImageFrameControl.batchReadyHint')}
                        </Typography>
                    </Box>
                    <SCUIButton
                        type="primary"
                        sizeType="default"
                        size="m"
                        text={t('studio.rawImageFrameControl.startBatch')}
                        disabled={startingBatch || loading || barcodeGroups.length === 0}
                        showStartIcon
                        startIcon={<PlayArrowOutlinedIcon />}
                        onClick={() => void handleStartBatch()}
                    />
                </Stack>
            </Paper>

            <Dialog
                open={Boolean(activeProductBarcode)}
                onClose={closeProductDialog}
                fullWidth
                maxWidth="lg"
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 2,
                            maxHeight: '92vh',
                        },
                    },
                }}
            >
                <DialogTitle sx={{ pr: 6 }}>
                    <Typography sx={{ fontWeight: 900, fontSize: '1.2rem' }}>
                        {activeProductBarcode}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('studio.rawImageFrameControl.productDialogHint')}
                    </Typography>
                    <IconButton
                        onClick={closeProductDialog}
                        sx={{ position: 'absolute', right: 12, top: 12 }}
                        aria-label={t('studio.rawImageFrameControl.closeDialog')}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {productDialogLoading ? (
                        <Box py={6}>
                            <SCUILoadingLinear />
                            <Typography variant="body2" color="text.secondary" textAlign="center" mt={2}>
                                {t('studio.rawImageFrameControl.loadingPhotos')}
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    md: 'repeat(3, minmax(0, 1fr))',
                                },
                                gap: 2,
                            }}
                        >
                            {PHOTO_TYPE_TABS.map((code) => renderFrameSlot(code, framesByType[code] ?? []))}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <Menu
                anchorEl={menuAnchor?.el ?? null}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
            >
                {menuAnchor &&
                    otherPhotoTypes(menuAnchor.item.photoTypeCode).map((code) => (
                        <MenuItem
                            key={code}
                            onClick={() => void handleMovePhotoType(menuAnchor.item, code)}
                        >
                            {t('studio.rawImageFrameControl.moveTo', {
                                target: getPhotoTypeLabel(code),
                            })}
                        </MenuItem>
                    ))}
            </Menu>
        </SCUIContent>
    );
};
